import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Download } from 'lucide-react';
import { LoadingSpinner } from '../../LoadingSpinner';
import {
  EmployeeOverviewMultiSelect,
  type EmployeeOverviewFilterOption,
} from '../overview/EmployeeOverviewMultiSelect';
import { EmployeePayrollPeriodPicker } from './EmployeePayrollPeriodPicker';

type PayrollExportKind = 'calendar' | 'bookings' | 'loans' | 'master_data';

type PayrollExportAvailability = {
  calendar: boolean;
  bookings: boolean;
  loans: boolean;
  /** Stammdatenänderungen hängen nicht an den Zeiteintrags-Issues (canExport). */
  masterData: boolean;
};

type EmployeePayrollToolbarProps = {
  from: string;
  to: string;
  onShift: (direction: -1 | 1) => void;
  onApplyCustomPeriod: (range: { from: string; to: string }) => void;
  filterEmployees: EmployeeOverviewFilterOption[];
  selectedEmployeeIds: string[];
  onSelectedEmployeeIdsChange: (ids: string[]) => void;
  canExport: boolean;
  exportAvailability: PayrollExportAvailability;
  exportBusy: boolean;
  previewLoading: boolean;
  onExportCalendar: () => void;
  onExportBookings: () => void;
  onExportLoans: () => void;
  onExportMasterData: () => void;
};

export function EmployeePayrollToolbar({
  from,
  to,
  onShift,
  onApplyCustomPeriod,
  filterEmployees,
  selectedEmployeeIds,
  onSelectedEmployeeIdsChange,
  canExport,
  exportAvailability,
  exportBusy,
  previewLoading,
  onExportCalendar,
  onExportBookings,
  onExportLoans,
  onExportMasterData,
}: EmployeePayrollToolbarProps) {
  const [exportOpen, setExportOpen] = React.useState(false);
  const exportTriggerRef = React.useRef<HTMLButtonElement>(null);
  const exportPanelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!exportOpen) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (exportTriggerRef.current?.contains(target) || exportPanelRef.current?.contains(target)) return;
      setExportOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [exportOpen]);

  const exportMenuBusy = exportBusy || previewLoading;
  // Agenda-Exporte sind bei Blocking-Issues gesperrt; Stammdatenänderungen bleiben exportierbar.
  const agendaExportsDisabled = exportMenuBusy || !canExport;

  const isExportKindDisabled = (kind: PayrollExportKind) => {
    if (kind === 'master_data') return exportMenuBusy || !exportAvailability.masterData;
    if (agendaExportsDisabled) return true;
    if (kind === 'calendar') return !exportAvailability.calendar;
    if (kind === 'bookings') return !exportAvailability.bookings;
    return !exportAvailability.loans;
  };

  const exportKinds: PayrollExportKind[] = ['calendar', 'bookings', 'loans', 'master_data'];
  const exportTriggerDisabled = exportKinds.every((kind) => isExportKindDisabled(kind));

  const runExport = (kind: PayrollExportKind) => {
    if (isExportKindDisabled(kind)) return;
    setExportOpen(false);
    if (kind === 'calendar') onExportCalendar();
    else if (kind === 'bookings') onExportBookings();
    else if (kind === 'loans') onExportLoans();
    else onExportMasterData();
  };

  const exportPanel =
    exportOpen && exportTriggerRef.current ? (
      <div
        ref={exportPanelRef}
        className="fixed z-[200] w-[380px] max-w-[calc(100vw-24px)] rounded-lg border border-app-border bg-app-surface py-1 shadow-lg"
        style={{
          top: exportTriggerRef.current.getBoundingClientRect().bottom + 6,
          right: window.innerWidth - exportTriggerRef.current.getBoundingClientRect().right,
        }}
        role="menu"
        aria-label="Export wählen"
      >
        {(
          [
            {
              kind: 'calendar' as const,
              title: 'Kalenderexport',
              description: 'Zeiteinträge für Agenda (Kalenderschlüssel, Stunden, Tage)',
            },
            {
              kind: 'bookings' as const,
              title: 'Lohnbuchungen',
              description:
                'Zulage, VMA, ÜN-Pauschale und Be-/Abzüge (ohne Darlehen) im Agenda-Format',
            },
            {
              kind: 'loans' as const,
              title: 'Darlehen',
              description: 'Darlehensauszahlung und -rückzahlung als separate Agenda-Datei',
            },
            {
              kind: 'master_data' as const,
              title: 'Stammdatenänderungen',
              description:
                'Geänderte Personalstammdaten im Zeitraum (alt → neu) inkl. Neuanlagen und Löschungen',
            },
          ] as const
        ).map((item, index) => (
          <button
            key={item.kind}
            type="button"
            role="menuitem"
            disabled={isExportKindDisabled(item.kind)}
            onClick={() => runExport(item.kind)}
            className={`flex w-full flex-col items-start gap-1 px-3.5 py-2.5 text-left hover:bg-app-hover disabled:cursor-not-allowed disabled:opacity-50 ${
              index > 0 ? 'border-t border-app-border' : ''
            }`}
          >
            <span className="text-xs font-semibold leading-none text-app-text">{item.title}</span>
            <span className="text-xs font-normal leading-snug text-app-text-subtle whitespace-normal">
              {item.description}
            </span>
          </button>
        ))}
      </div>
    ) : null;

  return (
    <div className="shrink-0 flex flex-wrap items-center gap-3 border-b border-app-border bg-app-surface px-6 py-3">
      <EmployeePayrollPeriodPicker
        from={from}
        to={to}
        onShift={onShift}
        onApplyCustomPeriod={onApplyCustomPeriod}
      />

      <EmployeeOverviewMultiSelect
        employees={filterEmployees}
        selected={selectedEmployeeIds}
        onChange={onSelectedEmployeeIdsChange}
      />

      <div className="flex-1 min-w-[8px]" />

      <button
        ref={exportTriggerRef}
        type="button"
        onClick={() => setExportOpen((v) => !v)}
        disabled={exportTriggerDisabled}
        className="inline-flex h-9 items-center gap-1.5 rounded-md bg-pipedrive-blue px-3.5 text-sm font-semibold text-white disabled:bg-app-surface-muted disabled:text-app-text-subtle"
        aria-expanded={exportOpen}
        aria-haspopup="menu"
      >
        {exportBusy ? <LoadingSpinner size="sm" /> : <Download className="h-3.5 w-3.5" />}
        Export vorbereiten
        <ChevronDown className="h-3.5 w-3.5 opacity-80" />
      </button>

      {exportOpen && typeof document !== 'undefined' ? createPortal(exportPanel, document.body) : null}
    </div>
  );
}
